import React, { memo, useRef, useEffect } from 'react';
import { SelectionItem } from './SelectionItem';

export const MiniCart = memo(function MiniCart({
    isOpen,
    selections,
    onClose,
    onRemove,
    onReviewClick,
}) {
    const panelRef = useRef(null);

    // Focus trap + Escape key
    useEffect(() => {
        if (!isOpen) return;

        const panel = panelRef.current;
        if (!panel) return;

        // Move focus into the panel
        const firstFocusable = panel.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;

            const focusable = [...panel.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )];
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <button
                    onClick={onClose}
                    aria-label="Close panel"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 9998,
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'opacity 0.3s',
                    }}
                />
            )}

            {/* Panel */}
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Saved Selections"
                tabIndex={-1}
                inert={!isOpen || undefined}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: 'max(33vw, 530px)',
                    maxWidth: '100dvw',
                    height: '100dvh',
                    background: '#ffffff',
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    outline: 'none',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#ffffff',
                }}>
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: '#1a1a1a',
                    }}>
                        SAVED SELECTIONS
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#666',
                            lineHeight: 1,
                            fontWeight: '300',
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px',
                    background: '#ffffff',
                }}>
                    {selections.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {selections.map(item => (
                                <SelectionItem
                                    key={item.id}
                                    item={item}
                                    onRemove={onRemove}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            color: '#999',
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '400',
                            }}>
                                No saved selections yet
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {selections.length > 0 && (
                    <div style={{
                        padding: '24px',
                        borderTop: '1px solid #e5e5e5',
                        background: '#ffffff',
                    }}>
                        <button
                            onClick={onReviewClick}
                            style={{
                                width: '100%',
                                padding: '14px 24px',
                                background: '#1a1a1a',
                                color: '#ffffff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#1a1a1a'}
                        >
                            Review with Our Team
                        </button>
                    </div>
                )}
            </div>
        </>
    );
});
